import React, { useRef } from 'react';

import { LUT_GROUPS, css, lutColor, lutLabel } from '../Viewer/lut';
import { ATTENTION_DEFAULTS, attentionAlpha, attentionKey, attentionView } from '../Viewer/attention';
import { CardTitle, LutRange, Slider } from './ChannelCard';

const num = (v, digits = 2) => v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const pct = v => `${Math.round(v * 100)}%`;
const value = v => (Math.abs(v) < 0.01 && v !== 0 ? v.toExponential(1) : num(v));
const signedValue = v => `${v < 0 ? '−' : v > 0 ? '+' : ''}${value(Math.abs(v))}`;

// Calage d'une carte, tel qu'enregistré (attentionFit.js).
function placementNote(map) {
  const p = map.placement || {};
  if (p.method === 'identity') return 'Coordinates in the pixels of the uploaded HES.';
  if (p.method === 'content') {
    return `Placed on the HES by fitting the patches to the tissue outline, then to what the HES shows under them (R² ${num(p.r2)}).`;
  }
  if (p.method === 'shape') return 'Placed on the HES by fitting the patches to the tissue outline only (approximate).';
  return '';
}

// Canal « HES + carte d'attention » (src/components/Viewer/attention.js), présenté comme une modalité : interrupteur
// (exclusif), carte affichée (si plusieurs), affichage (points chauds, carte complète ou isolignes), seuil des points
// chauds ou niveaux et épaisseur des isolignes, lissage, opacité, LUT (les isolignes ont leurs propres lissage, niveaux
// et LUT) ; importation d'un CSV de patchs, nouveau calage et suppression (contributeurs). Valeurs signées
// (contributions) : centrées sur 0, seuils et niveaux en part de l'échelle (scale : |valeur| des extrémités).
export default function AttentionCard({
  maps, map, settings, signed, valueName, scale, range, bounds, zero, ignored, coverage, busy, active, canWrite, canDelete,
  onChange,
  onToggle, onImport, onRefit, onDelete, collapsed, onCollapse, label, onRename,
}) {
  const fileRef = useRef(null);
  const isolines = settings.display === 'contours';
  const view = { ...attentionView(settings, signed), zero };  // lissage, seuil, niveaux et LUT du mode affiché
  const set = (key, v) => onChange({ [attentionKey(settings, key, signed)]: v });
  const defaults = attentionView({ ...ATTENTION_DEFAULTS, display: settings.display }, signed);
  const colorAt = t => {
    const c = lutColor(view.lut, [255, 255, 255], false, t, view.lutRange);
    const a = attentionAlpha(t, view);
    return c.map(v => 255 * (1 - a) + v * a);  // sur fond blanc, comme sur le verre du HES
  };
  const gradient = `linear-gradient(to right, ${Array.from({ length: 17 }, (_, i) => css(colorAt(i / 16))).join(', ')})`;
  const pick = e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) onImport(file);
  };
  return (
    <div className={`channel derived${active ? '' : ' off'}${collapsed ? ' collapsed' : ''}`}>
      <div className="channel-header">
        <span className="swatch attention" title="Computed channel: attention map over the HES" />
        <CardTitle name={label || 'HES + attention map'} collapsed={collapsed} onCollapse={onCollapse} onRename={onRename} />
        <label className="switch" title={map ? 'Show (hides the other modalities)' : 'Import an attention map first'}>
          <input type="checkbox" checked={active} disabled={!map} onChange={e => onToggle(e.target.checked)} />
          <span />
        </label>
      </div>
      {!collapsed && (
        <div className="channel-body compare">
          <p className="viewer-note" style={{ margin: 0 }}>
            {signed && settings.sBelow != null && bounds
              ? `Signed ${valueName} of each patch of an AI model, over the HES: colors from ${signedValue(bounds[0])} `
                + `(the threshold: first color of the LUT) to ${signedValue(bounds[1])} (98th percentile of |patch values|).`
              : signed
              ? `Signed ${valueName} of each patch of an AI model, over the HES: centered on 0 (with RdBu, blue below, `
                + `red above), scale ±${scale ? value(scale) : '…'} (98th percentile of |patch values|).`
              : `${valueName[0].toUpperCase()}${valueName.slice(1)} of a patch-based AI model over the HES. Colors follow `
                + 'the rank of each patch (percentile).'}
          </p>
          {maps.length > 1 && (
            <div className="compare-row">
              <span className="compare-label">Map</span>
              <select value={map?.id || ''} aria-label="Attention map" onChange={e => onChange({ o: e.target.value })}>
                {maps.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          )}
          {map && (
            <>
              <div className="compare-row">
                <span className="compare-label">Display</span>
                <select value={settings.display} aria-label="Attention display" onChange={e => onChange({ display: e.target.value })}>
                  <option value="hotspots">Hotspots (strongest values only)</option>
                  <option value="map">Full map</option>
                  <option value="contours">Isolines (tissue left visible)</option>
                </select>
              </div>
              {settings.display === 'hotspots' && (
                <Slider label={signed ? 'Hotspots from (share of the scale, either side of 0)' : 'Hotspots from the percentile'}
                  value={view.threshold} max={0.9} step={0.05} reset={defaults.threshold} format={pct}
                  onChange={v => set('threshold', v)} />
              )}
              {isolines && (
                <>
                  <Slider label={signed ? 'First isolines (± share of the scale)' : 'First isoline (percentile)'}
                    value={view.threshold} max={0.9} step={0.05} reset={defaults.threshold}
                    format={v => (signed ? `±${pct(v)}` : pct(v))} onChange={v => set('threshold', v)} />
                  <Slider label="Step between isolines" value={view.step} min={0.05} max={0.3} step={0.05}
                    reset={defaults.step} format={pct} onChange={v => set('step', v)} />
                  <Slider label="Line width (screen px; in a download, px of each level)" value={settings.lineWidth} min={0.5} max={16} step={0.5}
                    reset={ATTENTION_DEFAULTS.lineWidth} format={v => `${num(v, 1)} px`} onChange={v => onChange({ lineWidth: v })} />
                </>
              )}
              <Slider label="Smoothing (σ in patches; 0: raw patches)" value={view.sigma} max={4} step={0.25}
                reset={defaults.sigma} format={v => (v ? `σ ${num(v)}` : 'patches')} onChange={v => set('sigma', v)} />
              {signed && range[1] > range[0] && (
                <Slider label={`Ignore the most negative patches: ${valueName} below (counted as 0)`}
                  value={Math.min(Math.max(settings.sBelow ?? range[0], range[0]), range[1])} min={range[0]} max={range[1]}
                  step={(range[1] - range[0]) / 300} reset={range[0]}
                  format={v => (v <= range[0] ? 'off' : `${signedValue(v)} · ${ignored.toLocaleString('en-US')} ignored`)}
                  onChange={v => onChange({ sBelow: v <= range[0] ? null : v })} />
              )}
              {view.sigma > 0 && (
                <Slider label={`Keep strong patches when smoothing (weight ∝ |${signed ? valueName : 'rank'}|^p; 0: plain average)`}
                  value={settings.emphasis ?? 0} max={4} step={0.5} reset={ATTENTION_DEFAULTS.emphasis}
                  format={v => (v ? `p ${num(v, 1)}` : 'off')} onChange={v => onChange({ emphasis: v })} />
              )}
              <Slider label={isolines ? 'Line opacity' : 'Map opacity'} value={settings.opacity} max={1}
                reset={ATTENTION_DEFAULTS.opacity} onChange={v => onChange({ opacity: v })} />
              <div className="compare-row">
                <span className="compare-label">LUT</span>
                <select value={view.lut} aria-label="Attention LUT" onChange={e => set('lut', e.target.value)}>
                  {LUT_GROUPS.map(group => (
                    <optgroup key={group.label} label={group.label}>
                      {group.ids.map(id => <option key={id} value={id}>{lutLabel(id)}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <LutRange lut={view.lut} range={view.lutRange} onChange={r => set('lutRange', r)} />
              <div className="lut-bar" style={{ background: gradient }} />
              <div className="range-values">
                {signed
                  ? <><span>{bounds ? signedValue(bounds[0]) : ''}</span><span>0</span><span>{bounds ? signedValue(bounds[1]) : ''}</span></>
                  : <><span>Low {valueName}</span><span>High {valueName}</span></>}
              </div>
              <p className="viewer-note" style={{ margin: 0 }}>
                {map.name}: {map.count.toLocaleString('en-US')} patches of {Math.round(map.patchSize)} px
                {coverage != null && `, ${pct(coverage)} of the tissue`}. {placementNote(map)}
              </p>
            </>
          )}
          {!map && <p className="viewer-note" style={{ margin: 0 }}>No attention map for this sample yet.</p>}
          {busy && <p className="viewer-note" style={{ margin: 0 }}>{busy}</p>}
          {canWrite && !busy && (
            <>
              <p className="viewer-note" style={{ margin: 0 }}>
                CSV with columns x, y (patch corner, in pixels of the scanned slide) and a value per patch (attention,
                contribution…; signed values are shown centered on 0). The patches are placed on the HES automatically.
              </p>
              <div className="channel-options">
                <button type="button" className="chip" onClick={() => fileRef.current?.click()}>
                  {map ? 'Import another CSV…' : 'Import patches (CSV)…'}
                </button>
                {map && (
                  <button type="button" className="chip" onClick={() => onRefit(map)} title="Place the patches on the HES again">
                    Refit
                  </button>
                )}
                {map && canDelete(map) && (
                  <button type="button" className="chip" onClick={() => onDelete(map)}>Delete map</button>
                )}
              </div>
              <input ref={fileRef} type="file" accept=".csv,.tsv,.txt,text/csv" hidden onChange={pick} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
